/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styles from '../../command-palette.scss';
import classNames from 'classnames';
import { useRovingTabIndexContext } from '../../context/rovingTabIndexContext/RovingTabIndexContext';
import { sendTractiveMetric } from '@qualtrics/page-metrics/lib/tractiveService';

export const CommandPaletteItem = ({
  id,
  itemId,
  label,
  subLabel,
  breadcrumb,
  action,
  icon = null,
  searchTerm,
  category,
  projectType,
  suppressMetric = false,
}) => {
  const rovingContext = useRovingTabIndexContext();
  const itemRef = useRef(null);

  // Register this item with the roving tabindex context
  useEffect(() => {
    if (rovingContext && id) {
      rovingContext.registerItem(id, itemRef);
      return () => {
        rovingContext.unregisterItem(id);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const performAction = useCallback(() => {
    if (!suppressMetric) {
      sendTractiveMetric({
        billingTeam: 'GW',
        eventName: 'CommandPalette.Item.Action',
        customAttributes: {
          itemId,
          position: rovingContext.getItemPosition(id),
          usedSearch: searchTerm ? searchTerm.length > 0 : false,
          category,
          ...(projectType && { projectType }),
        },
      });
    }
    action();
  }, [
    id,
    itemId,
    action,
    rovingContext,
    searchTerm,
    category,
    projectType,
    suppressMetric,
  ]);

  return (
    <div
      ref={itemRef}
      onClick={performAction}
      role="button"
      tabIndex={rovingContext.getTabIndex(id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          performAction();
        }
      }}
      className={classNames(styles.commandPaletteItem, {
        [styles.withSubLabelItem]: !!subLabel,
      })}
    >
      <div
        className={classNames(styles.itemIconContainer, {
          [styles.withSubLabelIcon]: !!subLabel,
        })}
      >
        {icon?.Icon && (
          <icon.Icon
            className={styles.itemIcon}
            fill={icon?.color}
            aria-hidden
          />
        )}
      </div>
      <div className={styles.itemContent}>
        <span className={styles.itemLabel}>{label}</span>
        {subLabel && <span className={styles.itemSubLabel}>{subLabel}</span>}
        {breadcrumb && (
          <span className={styles.itemBreadcrumb}>{breadcrumb}</span>
        )}
      </div>
    </div>
  );
};

CommandPaletteItem.propTypes = {
  id: PropTypes.string.isRequired,
  itemId: PropTypes.string,
  label: PropTypes.string.isRequired,
  breadcrumb: PropTypes.string,
  subLabel: PropTypes.string,
  action: PropTypes.func.isRequired,
  icon: PropTypes.object,
  searchTerm: PropTypes.string,
  category: PropTypes.string,
  projectType: PropTypes.string,
  suppressMetric: PropTypes.bool,
};
